import type {
  BrandAssetBackground,
  BrandColorRoleViewDto,
  BrandRolesViewDto,
  BrandTokenRole,
  BrandTokenSetViewDto,
  BrandTypeRoleViewDto,
  BrandValueRoleViewDto,
  DesignSystemImportChangeDto,
  DesignSystemImportLogoChoice,
  DesignSystemImportLogoDto,
  DesignSystemImportPreviewDto,
  DesignSystemImportRoleChoice,
  DesignSystemImportRoleDto,
  DesignSystemImportRoleOptionDto,
  KnowledgeBaseAssetType,
  KnowledgeBaseFileViewDto,
} from '@/types/knowledgeBase'
import {
  COLOR_ROLES,
  dayOf,
  inWords,
  isReserved,
  ownTokens,
  ownTypeGroups,
  type ColorRole,
  type TypeRole,
  type ValueRole,
} from './brandGuidelinesModel'

/**
 * What the design-system import preview works out from the API's preview (ORB-1028): the choice for each logo found, the
 * questions about the roles the design system's names do not settle, the changes in words, and what stays as it is. Pure:
 * no fetching, no rendering. Both apps' import previews read it.
 *
 * ⚑ The API compares the zip with the brand at the level being viewed and lists every change. Nothing here compares
 * tokens itself; it groups and counts what the API listed, so the preview cannot disagree with the import.
 */

/** Where a logo found in the zip goes: one background, either, or nowhere because it is not a logo. */
export type Placement = 'light' | 'dark' | 'either' | 'none'

export interface LogoChoice {
  placement: Placement
  /** The slot it fills. Null until a person picks one for an image the API could not place. */
  assetType: KnowledgeBaseAssetType | null
}

export const PLACEMENTS: readonly { value: Placement; label: string }[] = [
  { value: 'light', label: 'For light backgrounds' },
  { value: 'dark', label: 'For dark backgrounds' },
  { value: 'either', label: 'Either background' },
  { value: 'none', label: 'Not a logo' },
]

/** The slots an imported image can fill. The social image is never imported: it is never marked for a background. */
export const IMPORT_SLOTS: readonly { type: KnowledgeBaseAssetType; label: string }[] = [
  { type: 'Logo', label: 'Logo' },
  { type: 'Icon', label: 'Icon' },
  { type: 'Banner', label: 'Banner' },
  { type: 'Favicon', label: 'Favicon' },
]

/** Each logo starts as the API's guess, which a person then confirms; an image it could not place starts as not a logo. */
export function initialChoices(logos: readonly DesignSystemImportLogoDto[]): Record<string, LogoChoice> {
  return Object.fromEntries(logos.map(logo => [logo.Path, logo.SuggestedAssetType
    ? { placement: placementOf(logo.SuggestedBackground ?? null), assetType: logo.SuggestedAssetType }
    : { placement: 'none' as const, assetType: null }]))
}

function placementOf(background: BrandAssetBackground | null): Placement {
  return background === 'Light' ? 'light' : background === 'Dark' ? 'dark' : 'either'
}

export function backgroundOf(placement: Placement): BrandAssetBackground | null {
  return placement === 'light' ? 'Light' : placement === 'dark' ? 'Dark' : null
}

/** The images a person has put somewhere without saying which slot they fill, which the import cannot send. */
export function unplacedLogos(logos: readonly DesignSystemImportLogoDto[], choices: Record<string, LogoChoice>): DesignSystemImportLogoDto[] {
  return logos.filter(logo => choices[logo.Path].placement !== 'none' && choices[logo.Path].assetType === null)
}

/** The choices as the import reads them: a slot and a background for each logo, or no slot for one left out. */
export function logoChoices(logos: readonly DesignSystemImportLogoDto[], choices: Record<string, LogoChoice>): DesignSystemImportLogoChoice[] {
  return logos.map(logo => {
    const choice = choices[logo.Path]
    if (choice.placement === 'none') return { Path: logo.Path, AssetType: null, Background: null }
    if (choice.assetType === null) throw new Error(`${logo.FileName} has no slot chosen, so it cannot be imported.`)
    return { Path: logo.Path, AssetType: choice.assetType, Background: backgroundOf(choice.placement) }
  })
}

/** The file a chosen logo replaces: the one already in that slot at this level. */
export function replacedFile(files: readonly KnowledgeBaseFileViewDto[], choice: LogoChoice): KnowledgeBaseFileViewDto | null {
  if (choice.placement === 'none' || choice.assetType === null) return null
  const background = backgroundOf(choice.placement)
  return files.find(file => file.AssetType === choice.assetType && (file.Background ?? null) === background) ?? null
}

/**
 * What the import leaves as it is at this level: the Guidelines words, the brand's documents, and every brand file whose
 * slot no chosen logo fills. The import replaces the colors, type and scales, and nothing else.
 */
export function keptItems(
  files: readonly KnowledgeBaseFileViewDto[],
  hasWords: boolean,
  logos: readonly DesignSystemImportLogoDto[],
  choices: Record<string, LogoChoice>,
): string[] {
  const replaced = new Set(logos.map(logo => replacedFile(files, choices[logo.Path])?.FileId).filter(Boolean))
  return [
    ...(hasWords ? ['the Guidelines words'] : []),
    ...files.filter(file => !replaced.has(file.FileId)).map(file => file.AssetType === 'OgImage' ? 'the social image' : file.FileName),
  ]
}

/**
 * What a logo becomes, in a sentence, and the file it takes the place of: "Becomes the Logo for light backgrounds, in place
 * of the current tidewell-logo.svg."
 */
export function becomes(choice: LogoChoice, replacing: string | null = null): string {
  if (choice.placement === 'none') return 'Left out of the brand.'
  const slot = IMPORT_SLOTS.find(candidate => candidate.type === choice.assetType)?.label
  if (!slot) return 'Choose which mark it is.'
  const background = backgroundOf(choice.placement)
  const where = background === null ? `the ${slot}, for either background` : `the ${slot} for ${background.toLowerCase()} backgrounds`
  return replacing === null ? `Becomes ${where}.` : `Becomes ${where}, in place of the current ${replacing}.`
}

// ── The roles the design system's names do not settle ──

/** Each role as its question names it, with what it is for in a line. Radius is Corners, the word a person uses. */
export const ROLE_LABELS: Record<BrandTokenRole, { label: string; purpose: string }> = {
  Primary: { label: 'Primary', purpose: 'Main brand color' },
  Secondary: { label: 'Secondary', purpose: 'Second brand color' },
  Accent: { label: 'Accent', purpose: 'Buttons and highlights' },
  Background: { label: 'Background', purpose: 'Email and page background' },
  Text: { label: 'Text', purpose: 'Body text color' },
  Heading: { label: 'Heading', purpose: 'Headings' },
  Body: { label: 'Body', purpose: 'Body text' },
  Radius: { label: 'Corners', purpose: 'Buttons and cards' },
  Shadow: { label: 'Shadow', purpose: 'Raised cards' },
}

/** The option a person chose for each role they were asked about, by name. A role not answered yet has none. */
export type RoleAnswers = Partial<Record<BrandTokenRole, string>>

/**
 * The roles a person answers before importing: those the design system's own names do not settle, and that it has tokens
 * for. ⚑ Nothing is chosen for them: the API refuses an import that leaves one unanswered. A role with no tokens to choose
 * from is not asked; the preview's Left out says why it stays unset.
 */
export function roleQuestions(roles: readonly DesignSystemImportRoleDto[]): DesignSystemImportRoleDto[] {
  return roles.filter(role => !role.Matched && role.Options.length > 0)
}

/** The roles taken from the design system's own names, which nobody is asked about. */
export function matchedRoles(roles: readonly DesignSystemImportRoleDto[]): { role: BrandTokenRole; option: DesignSystemImportRoleOptionDto }[] {
  return roles.flatMap(role => role.Matched ? [{ role: role.Role, option: role.Matched }] : [])
}

/** The questions not answered yet, in the preview's order. The import waits for every one. */
export function unansweredRoles(roles: readonly DesignSystemImportRoleDto[], answers: RoleAnswers): BrandTokenRole[] {
  return roleQuestions(roles).filter(role => !answers[role.Role]).map(role => role.Role)
}

/** What sits under a question's cards: every option the role can be that is not one of the likeliest. */
export function otherOptions(role: DesignSystemImportRoleDto): DesignSystemImportRoleOptionDto[] {
  const suggested = new Set(role.Suggested.map(option => option.Name))
  return role.Options.filter(option => !suggested.has(option.Name))
}

/** The import's role choices, one for each question, in the shape the API reads. */
export function roleChoices(roles: readonly DesignSystemImportRoleDto[], answers: RoleAnswers): DesignSystemImportRoleChoice[] {
  return roleQuestions(roles).map(role => {
    const name = answers[role.Role]
    if (!name) throw new Error(`${role.Role} has no token chosen, so it cannot be imported.`)
    return { Role: role.Role, Name: name }
  })
}

/** "Choose 3 things the design system doesn't say." */
export function questionsHeading(count: number): string {
  return `Choose ${count} ${count === 1 ? 'thing' : 'things'} the design system doesn't say`
}

/** What the questions are about, in a line under their heading. */
export function questionsLede(systemName: string, count: number): string {
  return `${systemName} names its colors and type its own way. ${count === 1 ? 'This' : 'These'} could be more than one of its tokens, so pick the right one.`
}

/** Why the import waits, said beside it: "Choose Primary and Corners before importing." Null once every one is answered. */
export function rolesWaitLine(unanswered: readonly BrandTokenRole[]): string | null {
  if (unanswered.length === 0) return null
  return `Choose ${inWords(unanswered.map(role => ROLE_LABELS[role].label))} before importing.`
}

/** How an option is drawn: a color as a swatch, a corner size and a shadow on a sample, a type style in words. */
export type RoleSample = 'color' | 'radius' | 'shadow' | 'type'

export function sampleOf(role: BrandTokenRole): RoleSample {
  if (role === 'Radius') return 'radius'
  if (role === 'Shadow') return 'shadow'
  return role === 'Heading' || role === 'Body' ? 'type' : 'color'
}

/**
 * The incoming roles as the import leaves them with the answers so far: each role asked about points at the option chosen
 * for it, or at nothing until one is.
 *
 * ⚑ The API's preview carries the roles as they stand before any choice, so the rest of the preview reads these instead,
 * or it would show a role unset that a person has just chosen.
 */
export function rolesWithAnswers(roles: BrandRolesViewDto, questions: readonly DesignSystemImportRoleDto[], answers: RoleAnswers): BrandRolesViewDto {
  const asked = new Map(roleQuestions(questions)
    .map(question => [question.Role, question.Options.find(option => option.Name === answers[question.Role]) ?? null] as const))
  if (asked.size === 0) return roles
  const pointing = (role: BrandTokenRole) => {
    const option = asked.get(role) ?? null
    return { option, at: { AliasChain: option ? [option.Name] : [], Problem: option ? null : roles[role].Problem } }
  }
  const color = (role: ColorRole): BrandColorRoleViewDto => {
    if (!asked.has(role)) return roles[role]
    const { option, at } = pointing(role)
    return { ...roles[role], ...at, Hex: option?.Value ?? null }
  }
  const type = (role: TypeRole): BrandTypeRoleViewDto => {
    if (!asked.has(role)) return roles[role]
    const { option, at } = pointing(role)
    return { ...roles[role], ...at, StyleName: option?.Name ?? null }
  }
  const value = (role: ValueRole): BrandValueRoleViewDto => {
    if (!asked.has(role)) return roles[role]
    const { option, at } = pointing(role)
    return { ...roles[role], ...at, Value: option?.Value ?? null }
  }
  return {
    Primary: color('Primary'),
    Secondary: color('Secondary'),
    Accent: color('Accent'),
    Background: color('Background'),
    Text: color('Text'),
    Heading: type('Heading'),
    Body: type('Body'),
    Radius: value('Radius'),
    Shadow: value('Shadow'),
  }
}

// ── The changes ──

export type ChangeFamily = 'Color' | 'Style' | 'Family' | 'Spacing' | 'Radius' | 'Shadow' | 'Role'

/**
 * The families a change can name, by the spellings the API's preview uses. A `Map`, because the name arrives off the wire:
 * an object literal would answer "constructor" (DESIGN.md §6).
 */
const FAMILIES = new Map<string, ChangeFamily>([
  ['color', 'Color'], ['colors', 'Color'],
  ['style', 'Style'], ['styles', 'Style'], ['typestyle', 'Style'], ['type', 'Style'],
  ['family', 'Family'], ['families', 'Family'], ['fontfamily', 'Family'], ['font', 'Family'],
  ['spacing', 'Spacing'], ['radius', 'Radius'], ['radii', 'Radius'], ['shadow', 'Shadow'], ['shadows', 'Shadow'],
  ['role', 'Role'], ['roles', 'Role'],
])

/** A change's family, or null for one this build does not know, which is then named as the API named it. */
export function familyOf(change: Pick<DesignSystemImportChangeDto, 'Family'>): ChangeFamily | null {
  return FAMILIES.get(change.Family.replace(/[\s_-]/g, '').toLowerCase()) ?? null
}

const NOUNS: Record<ChangeFamily, [string, string]> = {
  Color: ['color', 'colors'],
  Style: ['type style', 'type styles'],
  Family: ['font family', 'font families'],
  Spacing: ['spacing step', 'spacing steps'],
  Radius: ['corner size', 'corner sizes'],
  Shadow: ['shadow', 'shadows'],
  Role: ['role', 'roles'],
}

const ORDER: readonly ChangeFamily[] = ['Color', 'Style', 'Family', 'Spacing', 'Radius', 'Shadow', 'Role']

function counted(count: number, [one, many]: [string, string]): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The changes of one family by kind, in a phrase: "3 colors added, 2 changed". */
function familyPhrase(changes: readonly DesignSystemImportChangeDto[], noun: [string, string]): string {
  const of = (kind: string) => changes.filter(change => change.Kind === kind).length
  const parts = [['added', of('Added')], ['changed', of('Changed')], ['removed', of('Removed')]] as const
  const said = parts.filter(([, count]) => count > 0)
  return said.map(([verb, count], index) => index === 0 ? `${counted(count, noun)} ${verb}` : `${count} ${verb}`).join(', ')
}

/**
 * Every change in a line per family, then the logos a person chose to import: "3 colors added, 2 changed", "4 type styles
 * added", "4 logo files". Empty when the import changes nothing.
 */
export function changeLines(changes: readonly DesignSystemImportChangeDto[], logosChosen: number): string[] {
  const lines: string[] = []
  for (const family of ORDER) {
    const ofFamily = changes.filter(change => familyOf(change) === family)
    if (ofFamily.length > 0) lines.push(familyPhrase(ofFamily, NOUNS[family]))
  }
  const unknown = new Map<string, DesignSystemImportChangeDto[]>()
  for (const change of changes.filter(candidate => familyOf(candidate) === null)) {
    unknown.set(change.Family, [...(unknown.get(change.Family) ?? []), change])
  }
  for (const [family, ofFamily] of unknown) {
    const noun = family.toLowerCase()
    lines.push(familyPhrase(ofFamily, [noun, noun]))
  }
  if (logosChosen > 0) lines.push(counted(logosChosen, ['logo file', 'logo files']))
  return lines
}

/** The changes to one family, in the order the API listed them. */
export function changesOf(changes: readonly DesignSystemImportChangeDto[], family: ChangeFamily): DesignSystemImportChangeDto[] {
  return changes.filter(change => familyOf(change) === family)
}

/** The brand's own entries of a family that the import leaves as they are: in the zip and in no change. */
export function unchanged(names: readonly string[], changes: readonly DesignSystemImportChangeDto[], family: ChangeFamily): string[] {
  const changed = new Set(changesOf(changes, family).map(change => change.Name))
  return names.filter(name => !changed.has(name))
}

/** The incoming set's own names, by family, as the lists below the roles show them. */
export function incomingNames(set: BrandTokenSetViewDto): Record<'Color' | 'Style' | 'Family' | 'Spacing' | 'Radius' | 'Shadow', string[]> {
  return {
    Color: set.Colors.filter(token => !isReserved(token.Name)).map(token => token.Name),
    Style: ownTypeGroups(set.Type?.Groups).flatMap(group => group.Styles.map(style => style.Name)),
    Family: Object.keys(set.Type?.Families ?? {}),
    Spacing: ownTokens(set.Spacing).map(token => token.Name),
    Radius: ownTokens(set.Radius).map(token => token.Name),
    Shadow: ownTokens(set.Shadow).map(token => token.Name),
  }
}

/** The color roles that point at the same value after the import as before it. */
export function rolesKept(incoming: BrandTokenSetViewDto, current: BrandTokenSetViewDto | null): string[] {
  if (current === null) return []
  return COLOR_ROLES.map(({ role }) => role).filter(role => {
    const after = incoming.Roles[role]
    const before = current.Roles[role]
    return (after.AliasChain[0] ?? null) === (before.AliasChain[0] ?? null) && (after.Hex ?? null) === (before.Hex ?? null)
  })
}

/** What the preview compares with, in a sentence: the brand at this level as it is now. */
export function comparedWith(current: BrandTokenSetViewDto | null, levelName: string): string {
  if (current === null) return `${levelName} has no colors, type or spacing yet, so everything in the import is added.`
  const { Source, SourceName, ByName } = current.Provenance
  const day = dayOf(current.Provenance.At)
  const when = day ? `, ${day}` : ''
  if (Source === 'Website') return `Compared with Webster's draft${SourceName ? ` from ${SourceName}` : ''}${when}.`
  if (Source === 'Import') return `Compared with the brand imported from ${SourceName ?? 'a design system'}${when}.`
  return `Compared with the brand ${ByName ? `${ByName} set` : 'a person set'}${when}.`
}

/** What the zip carries that Brand Guidelines has no place for, by name: "tide-depth, tide-wash and Manrope.ttf." */
export function leftOutLine(dropped: DesignSystemImportPreviewDto['Dropped']): string | null {
  if (dropped.length === 0) return null
  return `${inWords(dropped.map(item => item.Name))}.`
}
